import React, { memo, useMemo } from 'react';
import { BaseTable } from '@/components/uiKit/Tables/BaseTable';
import { ColumnDef } from '@tanstack/react-table';
import { DataLayerGraph, DataLayerFilterConfig } from '@/types/shared/data-layers.types';

export interface TableDataLayerProps<T = any> {
  data: T[];
  columns: ColumnDef<T>[];
  title?: string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  enableSorting?: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  enableSearchDropdown?: boolean;
  defaultSortId?: string;
  wrapHeaders?: boolean;
  pageSize?: number;
  stickyFirstColumn?: boolean;
  variant?: 'dense' | 'spaced' | 'rounded' | 'base';
  enableVirtualization?: boolean;
  virtualItemHeight?: number;
  classNames?: any;
  graphs?: DataLayerGraph[];
  filters?: DataLayerFilterConfig[];
}

function TableDataLayerComponent<T = any>({
  data,
  columns,
  title,
  loading = false,
  onRowClick,
  enableSorting = true,
  enableSearch = true,
  searchPlaceholder,
  enableSearchDropdown,
  defaultSortId,
  wrapHeaders,
  pageSize = 20,
  stickyFirstColumn = false,
  variant = 'base',
  enableVirtualization,
  virtualItemHeight = 50,
  classNames,
}: TableDataLayerProps<T>) {
  // Memoize classNames to prevent unnecessary re-renders
  const memoizedClassNames = useMemo(() => 
    classNames || {
      card: {
        container: 'shadow-none rounded-none bg-transparent p-0',
      },
      table: {
        container: 'max-h-full',
        th: 'bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-400',
        td: 'text-sm',
      },
    }
  , [classNames]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-white dark:bg-gray-800 rounded-lg">
        <div className="text-gray-500">Loading data...</div>
      </div>
    );
  }

  // Auto-enable virtualization for large datasets
  const shouldVirtualize = enableVirtualization !== undefined 
    ? enableVirtualization 
    : data.length > 100;

  return (
    <>
      {title && <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{title}</h3>}
      <BaseTable
        data={data}
        columns={columns}
        rowClickHandler={onRowClick}
        enableSorting={enableSorting}
        enableSearch={enableSearch}
        searchPlaceholder={searchPlaceholder}
        enableSearchDropdown={enableSearchDropdown}
        defaultSortId={defaultSortId}
        wrapHeaders={wrapHeaders}
        pageSize={pageSize}
        variant={variant}
        stickyFirstColumn={stickyFirstColumn}
        hideHeaders={false}
        enableVirtualization={shouldVirtualize}
        virtualItemHeight={virtualItemHeight}
        classNames={memoizedClassNames}
      />
    </>
  );
}

export const TableDataLayer = memo(TableDataLayerComponent) as typeof TableDataLayerComponent;
